/**
 * crmFunnel/calculations.ts
 *
 * Pure calculation functions for the CRM Funnel module.
 *
 * Rules:
 *   - NEVER calculate rates from row-level values — always aggregate totals first.
 *   - Safe division everywhere: return 0 when denominator is 0.
 *   - All functions are pure and stateless (no side effects).
 *   - Rounding only happens in formatters, never here.
 */

import type { CrmLeadRow, CrmFunnelMetrics } from "./types";

// ─────────────────────────────────────────────
// SAFE DIVISION
// ─────────────────────────────────────────────

/**
 * Returns numerator / denominator, or 0 if denominator is 0 or NaN.
 * Prevents Infinity and NaN from propagating into metrics.
 */
export function safeDivide(numerator: number, denominator: number): number {
  if (!denominator || !isFinite(denominator)) return 0;
  return numerator / denominator;
}

// ─────────────────────────────────────────────
// AGGREGATE TOTALS
// ─────────────────────────────────────────────

export interface CrmRawTotals {
  leads: number;
  mqls: number;
  meetingsBooked: number;
  meetingsAttended: number;
  closedWonDeals: number;
  revenue: number;
}

/**
 * Sums raw funnel totals from an array of lead rows.
 * Returns zeros if rows array is empty.
 */
export function aggregateTotals(rows: CrmLeadRow[]): CrmRawTotals {
  const totals: CrmRawTotals = {
    leads:            0,
    mqls:             0,
    meetingsBooked:   0,
    meetingsAttended: 0,
    closedWonDeals:   0,
    revenue:          0,
  };

  for (const row of rows) {
    totals.leads            += 1;
    totals.mqls             += row.isMql ? 1 : 0;
    totals.meetingsBooked   += row.meetingsBooked;
    totals.meetingsAttended += row.meetingsAttended;
    totals.closedWonDeals   += row.isClosedWon ? 1 : 0;
    totals.revenue          += row.revenue;
  }

  return totals;
}

// ─────────────────────────────────────────────
// DERIVE METRICS FROM TOTALS
// ─────────────────────────────────────────────

/**
 * Derives all CrmFunnelMetrics from pre-aggregated raw totals.
 * All rate calculations happen here — never on individual rows.
 *
 * Rate definitions:
 *   leadQualificationRate = mqls / leads * 100
 *   bookingRate           = meetingsBooked / mqls * 100
 *   showRate              = meetingsAttended / meetingsBooked * 100
 *   closeRate             = closedWonDeals / meetingsAttended * 100
 *   revenuePerLead        = revenue / leads
 *   revenuePerOpportunity = revenue / closedWonDeals
 */
export function deriveMetrics(totals: CrmRawTotals): CrmFunnelMetrics {
  return {
    leads:            totals.leads,
    mqls:             totals.mqls,
    meetingsBooked:   totals.meetingsBooked,
    meetingsAttended: totals.meetingsAttended,
    closedWonDeals:   totals.closedWonDeals,
    revenue:          totals.revenue,

    leadQualificationRate:  safeDivide(totals.mqls,             totals.leads)             * 100,
    bookingRate:            safeDivide(totals.meetingsBooked,   totals.mqls)              * 100,
    showRate:               safeDivide(totals.meetingsAttended, totals.meetingsBooked)    * 100,
    closeRate:              safeDivide(totals.closedWonDeals,   totals.meetingsAttended)  * 100,
    revenuePerLead:         safeDivide(totals.revenue,          totals.leads),
    revenuePerOpportunity:  safeDivide(totals.revenue,          totals.closedWonDeals),
  };
}

// ─────────────────────────────────────────────
// PERCENTAGE CHANGE
// ─────────────────────────────────────────────

/**
 * Calculates percentage change between current and previous values.
 * Returns 0 if previous is 0 (no baseline to compare against).
 */
export function percentChange(current: number, previous: number): number {
  if (!previous) return 0;
  return safeDivide(current - previous, previous) * 100;
}

/**
 * Builds a changes record for all CrmFunnelMetrics keys.
 */
export function buildChanges(
  current: CrmFunnelMetrics,
  previous: CrmFunnelMetrics
): Record<keyof CrmFunnelMetrics, number> {
  const keys = Object.keys(current) as Array<keyof CrmFunnelMetrics>;
  const result = {} as Record<keyof CrmFunnelMetrics, number>;
  for (const key of keys) {
    result[key] = percentChange(current[key], previous[key]);
  }
  return result;
}

// ─────────────────────────────────────────────
// VALIDATION UTILITY
// ─────────────────────────────────────────────

export interface CrmValidationCheck {
  field: string;
  expected: number;
  actual: number;
  delta: number;
  passed: boolean;
}

export interface CrmValidationResult {
  passed: boolean;
  checks: CrmValidationCheck[];
}

/**
 * Verifies that metrics derived from rows match directly-aggregated totals.
 * Useful for catching calculation bugs or data integrity issues.
 */
export function validateMetrics(
  rows: CrmLeadRow[],
  metrics: CrmFunnelMetrics,
  tolerance = 0.001
): CrmValidationResult {
  const totals = aggregateTotals(rows);
  const TOLERANCE = tolerance;

  const checks: CrmValidationCheck[] = [
    { field: "leads",            expected: totals.leads,            actual: metrics.leads },
    { field: "mqls",             expected: totals.mqls,             actual: metrics.mqls },
    { field: "meetingsBooked",   expected: totals.meetingsBooked,   actual: metrics.meetingsBooked },
    { field: "meetingsAttended", expected: totals.meetingsAttended, actual: metrics.meetingsAttended },
    { field: "closedWonDeals",   expected: totals.closedWonDeals,   actual: metrics.closedWonDeals },
    { field: "revenue",          expected: totals.revenue,          actual: metrics.revenue },
  ].map(c => ({
    ...c,
    delta:  Math.abs(c.expected - c.actual),
    passed: Math.abs(c.expected - c.actual) < TOLERANCE,
  }));

  return {
    passed: checks.every(c => c.passed),
    checks,
  };
}
